import { TThreaTab } from "@/components/organism/ThreadHistory/types";
import { componentMap } from "@/routes/componentRoutes";
import { LayoutItem } from "@/store/layout/sidebarToContent";

export const IS_IFRAME_ROUTE = '/fleet-assistant-integration';

const otherComponents = ['chat', 'about', 'feedback', 'onboardingMode'] as const;

const otherSet = new Set<string>(otherComponents);

const validSections = new Set(Object.keys(componentMap) as Array<keyof typeof componentMap>);

export const isValidSection = (section?: string): section is keyof typeof componentMap => {
    return !!section && validSections.has(section as keyof typeof componentMap);
};

export const isCorrectChatNavigation = (route: string, subRoute?: string): boolean => {
    return !(otherSet.has(route) && subRoute);

};

export const getNavigationRedirect = (route: string): string => {
    if (otherSet.has(route)) return `/${route}`;
    return `/${route}`;
};

export type NavigationDecision =
    | { type: 'set-layout'; layout: LayoutItem }
    | { type: 'redirect'; to: string, state?: Record<string, string> }
    | { type: 'fallback' }
    | { type: 'special-route' };

// INFO: Deprecated route combinations that should redirect to a new path carrying state if necessary.
// The key must be "section/subsection" or "section" for single-segment routes.

const FAVORITES_REDIRECT = { to: '/chat', state: { defaultTab: 'favoriteChats' } } as const;

const DEPRECATED_ROUTES: Record<string, { to: string; state?: Record<string, TThreaTab> }> = {
  'chat/favorites': FAVORITES_REDIRECT,
  'favorites': FAVORITES_REDIRECT,
};

/**
 * Builds a lookup key from the current route segments.
 */
const buildRouteKey = (section?: string, subsection?: string): string =>
  subsection ? `${section}/${subsection}` : section ?? '';

export const resolveNavigation = (
    section?: string,
    subsection?: string,
    isIframeTestRoute?: boolean,
): NavigationDecision => {

    const deprecatedRedirect = DEPRECATED_ROUTES[buildRouteKey(section, subsection)];
    
    if (deprecatedRedirect) {
        return { type: 'redirect', ...deprecatedRedirect };
    }

    if (isIframeTestRoute) {
        return { type: 'special-route' };
    }

    if (isValidSection(subsection)) {
        if (section === 'chat' && otherSet.has(subsection)) {
            return { type: 'redirect', to: getNavigationRedirect(subsection) };
        }
        return { type: 'set-layout', layout: subsection };
    }

    if (isValidSection(section)) {
        if (subsection && !isValidSection(subsection)) {
            return { type: 'redirect', to: getNavigationRedirect(section) };
        }

        if (!isCorrectChatNavigation(section, subsection)) {
            return { type: 'redirect', to: getNavigationRedirect(section) };
        }

        return { type: 'set-layout', layout: section };
    }

    return { type: 'redirect', to: "/chat" };
};
